import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Link } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { VolumeChart2 } from 'components/well-page-content/decline-curve-analysis/volume-chart-2';
import { MineralType } from 'types/minerals';
import { XAxisType } from 'components/well-page-content/types';
import type { PerWellChartData } from './types';
import type { VolumeDisplayMode } from 'components/well-page-content';
import styles from './well-analysis-chart.module.scss';

interface WellAnalysisChartProps {
  uwi: string;
  wellName?: string;
  chartData?: PerWellChartData;
  chartType?: 'monthly' | 'logarithmic' | 'cumulative' | 'ratio';
  selectedSeries?: Array<MineralType | 'gor' | 'wor' | 'wgr' | 'cgr'>;
  showMarkers?: { segmentation: boolean; outlier: boolean };
  showTypeCurve?: boolean;
  xAxisType?: XAxisType;
  /** Monthly volumes or daily rates, for the monthly and log charts. */
  volumeDisplayMode?: VolumeDisplayMode;
  /** Whether the production volumes are already daily, so the Daily display leaves them as they are. */
  productionVolumeIsDailyRate?: boolean;
  height?: number;
  selected?: boolean;
  onSelectedChange: (uwi: string, selected: boolean) => void;
  onEdit?: (uwi: string) => void;
}

const NOOP = () => {};
const NOOP_DRAG = () => {};
const DEFAULT_MARKERS = { segmentation: false, outlier: false };
const DEFAULT_SERIES: MineralType[] = ['oil', 'gas', 'water'];
const DEFAULT_CHART_DATA: PerWellChartData = {
  production: {
    volume: [],
    cumulative: [],
    ratio: [],
    materialBalanceTime: [],
  },
  forecasts: [],
  typeCurveVolumes: null,
  isLoading: true,
};

export const WellAnalysisChart = memo(
  ({
    uwi,
    wellName,
    chartData = DEFAULT_CHART_DATA,
    chartType = 'monthly',
    selectedSeries = DEFAULT_SERIES,
    showMarkers = DEFAULT_MARKERS,
    showTypeCurve = false,
    xAxisType,
    volumeDisplayMode = 'monthly',
    productionVolumeIsDailyRate = false,
    height,
    selected = false,
    onSelectedChange,
    onEdit,
  }: WellAnalysisChartProps) => {
    const [isSelected, setIsSelected] = useState(selected);

    useEffect(() => {
      setIsSelected(selected);
    }, [selected]);

    const handleCheckboxChange = useCallback(
      (checked: boolean) => {
        setIsSelected(checked);
        onSelectedChange?.(uwi, checked);
      },
      [uwi, onSelectedChange]
    );

    const handleEditClick = useCallback(() => {
      onEdit?.(uwi);
    }, [uwi, onEdit]);

    const title = wellName ? `UWI ${uwi} — ${wellName}` : `UWI ${uwi}`;

    return (
      <VolumeChart2
        type={chartType}
        height={height}
        production={chartData.production}
        productionVolumeIsDailyRate={productionVolumeIsDailyRate}
        volumeDisplayMode={volumeDisplayMode}
        typeCurveVolumes={chartData.typeCurveVolumes}
        forecasts={chartData.forecasts}
        selectedSeries={selectedSeries}
        isLoading={chartData.isLoading}
        showMarkers={showMarkers}
        showTypeCurve={showTypeCurve}
        xAxisType={xAxisType}
        onDragEnd={NOOP}
        onDraggableSeriesUpdate={NOOP_DRAG}
        initialAutoZoom
        // Tint the download icon to match the header's edit (pencil) icon.
        exportConfig={{
          label: title,
          filenamePrefix: `volume-${uwi}`,
          iconClassName: styles.downloadIcon,
        }}
        header={
          <ChartHeader
            uwi={uwi}
            title={title}
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            onEdit={handleEditClick}
          />
        }
      />
    );
  }
);

WellAnalysisChart.displayName = 'WellAnalysisChart';

interface ChartHeaderProps {
  uwi: string;
  title: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  onEdit: () => void;
}

const ChartHeader = ({
  uwi,
  title,
  checked,
  onCheckedChange,
  onEdit,
}: ChartHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.headerCheckbox}>
      <Checkbox
        id={`well-select-${uwi}`}
        labelText=""
        checked={checked}
        onChange={(_evt: any, { checked }: { checked: boolean }) =>
          onCheckedChange(checked)
        }
      />
    </div>
    <Link href={`/wells/${uwi}`} target="_blank" className={styles.headerTitle}>
      {title}
      {/* <span className={styles.headerTitle} title={title}>
    </span> */}
    </Link>
    <Button
      kind="ghost"
      size="sm"
      hasIconOnly
      renderIcon={Edit}
      iconDescription="Edit DCA Forecast"
      tooltipPosition="left"
      onClick={onEdit}
      className={styles.editIcon}
    />
  </div>
);
